import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userManualAPI } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  BookOpen,
  Download,
  FileText,
  Edit3,
  Save,
  X,
  ChevronRight,
  Printer,
} from 'lucide-react';

/* ────────────────────────────────────────────
   Simple markdown-ish renderer
   Supports: ## Heading, - bullet, plain text
   ──────────────────────────────────────────── */
const RenderContent = ({ content }) => {
  if (!content) return null;
  const lines = content.split('\n');
  const elements = [];
  let key = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      elements.push(<div key={key++} className="h-3" />);
    } else if (trimmed.startsWith('## ')) {
      elements.push(
        <h3 key={key++} className="text-base font-semibold text-slate-800 mt-5 mb-2">
          {trimmed.slice(3)}
        </h3>
      );
    } else if (trimmed.startsWith('# ')) {
      elements.push(
        <h2 key={key++} className="text-lg font-bold text-slate-900 mt-6 mb-3">
          {trimmed.slice(2)}
        </h2>
      );
    } else if (trimmed.startsWith('- ')) {
      elements.push(
        <div key={key++} className="flex items-start gap-2 ml-4 mb-1">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0" />
          <span className="text-sm text-slate-700 leading-relaxed">{trimmed.slice(2)}</span>
        </div>
      );
    } else {
      elements.push(
        <p key={key++} className="text-sm text-slate-700 leading-relaxed mb-1">
          {trimmed}
        </p>
      );
    }
  }
  return <div>{elements}</div>;
};

/* ────────────────────────────────────────────
   Main Page
   ──────────────────────────────────────────── */
export default function UserManualPage() {
  const { isAdmin } = useAuth();
  const [sections, setSections] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewAll, setViewAll] = useState(false);

  const fetchSections = useCallback(async () => {
    try {
      const res = await userManualAPI.listSections();
      const data = res.data;
      setSections(data);
      if (!activeKey && data.length > 0) {
        setActiveKey(data[0].key);
      }
    } catch {
      toast.error('Failed to load manual sections');
    } finally {
      setLoading(false);
    }
  }, [activeKey]);

  useEffect(() => { fetchSections(); }, [fetchSections]);

  const activeSection = sections.find(s => s.key === activeKey);

  const handleEdit = () => {
    if (!activeSection) return;
    setEditContent(activeSection.content || '');
    setEditTitle(activeSection.title || '');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!activeSection) return;
    setSaving(true);
    try {
      await userManualAPI.updateSection(activeKey, { title: editTitle, content: editContent });
      toast.success('Section saved');
      setEditing(false);
      await fetchSections();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadSection = (key) => {
    userManualAPI.downloadSectionPdf(key);
  };

  const handleDownloadFull = () => {
    userManualAPI.downloadFullPdf();
  };

  const handleViewPdf = async (key) => {
    const newWindow = window.open('about:blank', '_blank');
    try {
      const res = await userManualAPI.viewSectionPdf(key);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      if (newWindow) {
        newWindow.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch {
      if (newWindow) newWindow.close();
      toast.error('Failed to open PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900" />
      </div>
    );
  }

  return (
    <div data-testid="user-manual-page">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1
            className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="user-manual-title"
          >
            USER MANUAL
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Complete guide to using the RTMS PRO system
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={viewAll ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setViewAll(!viewAll); setEditing(false); }}
            data-testid="toggle-view-all-btn"
          >
            <BookOpen className="w-4 h-4 mr-1" />
            {viewAll ? 'Section View' : 'View All'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadFull} data-testid="download-full-pdf-btn">
            <Download className="w-4 h-4 mr-1" />
            Download Full Manual
          </Button>
        </div>
      </div>

      {viewAll ? (
        /* ── Combined / View-All mode ── */
        <div className="bg-white rounded-sm border border-slate-200 p-6 md:p-10 max-w-4xl mx-auto">
          <h2
            className="text-xl font-bold text-slate-900 mb-6"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            RTMS PRO USER MANUAL
          </h2>
          {/* TOC */}
          <div className="mb-8 p-4 bg-slate-50 rounded-sm border border-slate-100">
            <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">
              Table of Contents
            </h3>
            <div className="space-y-1">
              {sections.map((s, i) => (
                <button
                  key={s.key}
                  onClick={() => {
                    document.getElementById(`section-${s.key}`)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors w-full text-left"
                >
                  <span className="text-slate-400 w-6 text-right">{i + 1}.</span>
                  <span>{s.title}</span>
                </button>
              ))}
            </div>
          </div>
          {/* Sections */}
          {sections.map((s, i) => (
            <div key={s.key} id={`section-${s.key}`} className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-slate-900">
                  {i + 1}. {s.title}
                </h2>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleViewPdf(s.key)}
                    title="View as PDF"
                    data-testid={`view-pdf-${s.key}`}
                  >
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownloadSection(s.key)}
                    title="Download PDF"
                    data-testid={`download-pdf-${s.key}`}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="border-b border-slate-200 mb-3" />
              <RenderContent content={s.content} />
            </div>
          ))}
        </div>
      ) : (
        /* ── Section-by-section mode ── */
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: section list */}
          <div className="lg:w-72 shrink-0">
            <div className="bg-white rounded-sm border border-slate-200 overflow-hidden lg:sticky lg:top-20">
              <div className="p-3 border-b border-slate-100 bg-slate-50">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Sections
                </span>
              </div>
              <div className="max-h-60 lg:max-h-[calc(100vh-220px)] overflow-y-auto p-1">
                {sections.map((s, i) => (
                  <button
                    key={s.key}
                    onClick={() => { setActiveKey(s.key); setEditing(false); }}
                    data-testid={`manual-section-${s.key}`}
                    className={`flex items-center gap-2 w-full px-3 py-2 rounded-sm text-left text-sm transition-colors ${
                      activeKey === s.key
                        ? 'bg-slate-900 text-white font-medium'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <span className={`w-5 text-right text-xs ${activeKey === s.key ? 'text-slate-400' : 'text-slate-400'}`}>
                      {i + 1}.
                    </span>
                    <span className="flex-1 truncate">{s.title}</span>
                    {activeKey === s.key && <ChevronRight className="w-4 h-4 shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: content area */}
          <div className="flex-1 min-w-0">
            {activeSection ? (
              <div className="bg-white rounded-sm border border-slate-200">
                {/* Section header */}
                <div className="flex items-center justify-between p-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <h2 className="text-lg font-bold text-slate-900">{activeSection.title}</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewPdf(activeKey)}
                      title="View as PDF"
                      data-testid="view-section-pdf-btn"
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadSection(activeKey)}
                      title="Download PDF"
                      data-testid="download-section-pdf-btn"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    {isAdmin && !editing && (
                      <Button variant="outline" size="sm" onClick={handleEdit} data-testid="edit-section-btn">
                        <Edit3 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                </div>

                {/* Content / Editor */}
                <div className="p-5 md:p-8">
                  {editing ? (
                    <div className="space-y-4" data-testid="section-editor">
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                          Section Title
                        </label>
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="edit-section-title"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                          Content
                        </label>
                        <div className="mb-2 p-2 bg-slate-50 rounded-sm border border-slate-100">
                          <p className="text-xs text-slate-500">
                            <strong>Formatting:</strong> Use <code className="bg-slate-200 px-1 rounded">## </code>
                            for headings, <code className="bg-slate-200 px-1 rounded">- </code> for bullet points.
                            Leave a blank line for spacing.
                          </p>
                        </div>
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          rows={24}
                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-slate-900 resize-y"
                          data-testid="edit-section-content"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-2">
                        <Button onClick={handleSave} disabled={saving} data-testid="save-section-btn">
                          <Save className="w-4 h-4 mr-1" />
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div data-testid="section-content">
                      <RenderContent content={activeSection.content} />
                      {activeSection.updated_by && activeSection.updated_by !== 'system' && (
                        <div className="mt-8 pt-4 border-t border-slate-100">
                          <p className="text-xs text-slate-400">
                            Last updated by {activeSection.updated_by}{' '}
                            {activeSection.updated_at &&
                              `on ${new Date(activeSection.updated_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Select a section to view</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
